// C12 — search-syntax discoverability.
//
// searchParser.js supports eight field prefixes, and the only place any of them
// was mentioned was a truncated hint inside two placeholders ("try cage:Blue-42
// or strain:C57"). The litters search did not even have that. So most of the
// search capability was undiscoverable unless you read the source.
//
// The prefix list is derived from searchParser's PARAM_MAP rather than retyped,
// so a prefix added there cannot silently go undocumented here.
import React, { useEffect, useRef, useState } from 'react';

// One example per prefix. Keys must match searchParser's canonical field names.
const PREFIX_HELP = {
  cage:     { label: 'Cage number',   example: 'cage:Blue-42' },
  mouse:    { label: 'Mouse tag',     example: 'mouse:1032', alias: 'tag:' },
  strain:   { label: 'Strain',        example: 'strain:C57' },
  location: { label: 'Cage location', example: 'location:Room 3' },
  sex:      { label: 'Sex',           example: 'sex:Female' },
  locus:    { label: 'Genotype locus',  example: 'locus:Cre' },
  allele:   { label: 'Genotype allele', example: 'allele:+/-' },
  notes:    { label: 'Notes',         example: 'notes:sick' },
};

/**
 * A "?" button that reveals the supported search prefixes.
 *
 * @param {string[]} [fields] - which prefixes apply here, in display order.
 *   Defaults to all of them. Cages and litters do not support every field, and
 *   listing one that silently does nothing is worse than omitting it.
 */
export function SearchHelp({ fields = Object.keys(PREFIX_HELP) }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click or Escape — a popover that can only be dismissed by
  // the button that opened it is a trap on touch devices.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const shown = fields.filter(f => PREFIX_HELP[f]);

  return (
    <span ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label="Search help"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          width: '1.6rem', height: '1.6rem', padding: 0, borderRadius: '50%',
          border: '1px solid #ccc', background: '#fff', color: '#555',
          cursor: 'pointer', lineHeight: 1, flexShrink: 0,
        }}
      >
        ?
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Search syntax"
          style={{
            position: 'absolute', top: '2rem', right: 0, zIndex: 50,
            width: 'min(20rem, 90vw)', padding: '0.75rem',
            background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)', fontSize: '0.8rem',
            color: '#333', textAlign: 'left',
          }}
        >
          <strong style={{ display: 'block', marginBottom: '0.4rem' }}>Search prefixes</strong>
          <p style={{ margin: '0 0 0.5rem', color: '#666' }}>
            Combine freely; anything without a prefix searches every field.
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {shown.map(field => (
                <tr key={field}>
                  <td style={{ padding: '0.15rem 0.4rem 0.15rem 0', whiteSpace: 'nowrap', color: '#666' }}>
                    {PREFIX_HELP[field].label}
                  </td>
                  <td style={{ padding: '0.15rem 0' }}>
                    <code>{PREFIX_HELP[field].example}</code>
                    {PREFIX_HELP[field].alias && (
                      <span style={{ color: '#999' }}> (or {PREFIX_HELP[field].alias})</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ margin: '0.5rem 0 0', color: '#666' }}>
            Use quotes for values with spaces: <code>cage:"Blue 42"</code>
          </p>
        </div>
      )}
    </span>
  );
}

export default SearchHelp;
